import React, { useState, useEffect } from 'react'
import { Box } from '@mui/material'
import Image from 'next/image'

interface HeroBannerCarouselProps {
    height?: string
    minHeight?: number
    children?: React.ReactNode
}

const HeroBannerCarousel: React.FC<HeroBannerCarouselProps> = ({ height = '55vh', minHeight = 350, children }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const [isTransitioning, setIsTransitioning] = useState(false)

    const images = [
        {
            src: 'https://ui.cltp.co/offers-ui/OfferPages/offers/cms/GreenMounts.jpg',
            alt: 'Green Mountains'
        },
        {
            src: 'https://ui.cltp.co/offers-ui/OfferPages/offers/cms/FlightClouds.jpg',
            alt: 'Flight Clouds'
        },
        {
            src: 'https://ui.cltp.co/offers-ui/OfferPages/offers/cms/LakeBoats.jpg',
            alt: 'Lake Boats'
        }
    ]

    useEffect(() => {
        const interval = setInterval(() => {
            setIsTransitioning(true)
            setTimeout(() => {
                setCurrentImageIndex(prevIndex => (prevIndex + 1) % images.length)
                setIsTransitioning(false)
            }, 400) // Half of transition duration
        }, 6000) // Change image every 6 seconds

        return () => clearInterval(interval)
    }, [images.length])

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height,
                minHeight,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                zIndex: 0
            }}
        >
            {/* Current Image */}
            <Image
                src={images[currentImageIndex].src}
                alt={images[currentImageIndex].alt}
                fill
                style={{
                    objectFit: 'cover',
                    objectPosition: 'center',
                    opacity: isTransitioning ? 0.7 : 1,
                    transition: 'opacity 0.8s ease-in-out'
                }}
                priority
            />

            {/* Gradient Overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 100%)',
                    zIndex: 1
                }}
            />

            {/* Carousel Indicators */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1,
                    zIndex: 10
                }}
            >
                {images.map((_, index) => (
                    <Box
                        key={index}
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: index === currentImageIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer',
                            transition: 'all 0.5s ease-in-out',
                            transform: index === currentImageIndex ? 'scale(1.2)' : 'scale(1)',
                            '&:hover': {
                                backgroundColor: 'white',
                                transform: 'scale(1.1)'
                            }
                        }}
                        onClick={() => {
                            if (index !== currentImageIndex) {
                                setIsTransitioning(true)
                                setTimeout(() => {
                                    setCurrentImageIndex(index)
                                    setIsTransitioning(false)
                                }, 400)
                            }
                        }}
                    />
                ))}
            </Box>

            {/* Children Content */}
            {children}
        </Box>
    )
}

export default HeroBannerCarousel
